import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { RefreshCw, Download, ArrowLeft, Upload, X, CheckCircle, Image as ImageIcon, Shield, Zap, Maximize2, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import apiClient from '../lib/api';
import { getSessionId } from '../lib/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get or create session ID
const ResizeImagePage = () => {
  const { toast } = useToast();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [originalDimensions, setOriginalDimensions] = useState(null);
  const [resizeMode, setResizeMode] = useState('dimensions');
  const [width, setWidth] = useState('800');
  const [height, setHeight] = useState('600');
  const [percentage, setPercentage] = useState('50');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [resizeResult, setResizeResult] = useState(null);
  const [resizedPreview, setResizedPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [openFaq, setOpenFaq] = useState(null);
  const fileInputRef = useRef(null);

  const presetSizes = [
    { name: 'Instagram Square', width: 1080, height: 1080 },
    { name: 'Facebook Cover', width: 1200, height: 630 },
    { name: 'Twitter Header', width: 1500, height: 500 },
    { name: 'YouTube Thumbnail', width: 1280, height: 720 },
  ];

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please upload a valid image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size too large. Maximum 10MB allowed",
        variant: "destructive",
      });
      return;
    }

    setUploadingFile(true);
    
    try {
      const dimensions = await getImageDimensions(file);
      
      const imageData = {
        file: file,
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
        preview: URL.createObjectURL(file)
      };

      setUploadedImage(imageData);
      setOriginalDimensions(dimensions);
      setWidth(dimensions.width.toString());
      setHeight(dimensions.height.toString());
      setResizeResult(null);
      setResizedPreview(null);
      
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveImage = () => {
    if (uploadedImage?.preview) {
      URL.revokeObjectURL(uploadedImage.preview);
    }
    if (resizedPreview) {
      URL.revokeObjectURL(resizedPreview);
    }
    setUploadedImage(null);
    setOriginalDimensions(null);
    setResizeResult(null);
    setResizedPreview(null);
  };

  const handlePresetSelect = (preset) => {
    setWidth(preset.width.toString());
    setHeight(preset.height.toString());
    setResizeMode('dimensions');
    setMaintainAspectRatio(false);
  };

  const handleWidthChange = (value) => {
    setWidth(value);
    if (maintainAspectRatio && originalDimensions) {
      const ratio = originalDimensions.height / originalDimensions.width;
      setHeight(Math.round(parseInt(value) * ratio).toString());
    }
  };

  const handleHeightChange = (value) => {
    setHeight(value);
    if (maintainAspectRatio && originalDimensions) {
      const ratio = originalDimensions.width / originalDimensions.height;
      setWidth(Math.round(parseInt(value) * ratio).toString());
    }
  };

  const handleResize = async () => {
    if (!uploadedImage) {
      toast({
        title: "Error",
        description: "Please upload an image to resize",
        variant: "destructive",
      });
      return;
    }

    if (resizeMode === 'dimensions' && (!width || !height)) {
      toast({
        title: "Error",
        description: "Please enter valid dimensions",
        variant: "destructive",
      });
      return;
    }

    if (resizeMode === 'percentage' && !percentage) {
      toast({
        title: "Error",
        description: "Please enter a valid percentage",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedImage.file);
      
      if (resizeMode === 'dimensions') {
        formData.append('width', width);
        formData.append('height', height);
      } else {
        formData.append('percentage', percentage);
      }

      const response = await apiClient.post(
        `${BACKEND_URL}/api/image/resize?session_id=${sessionId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      setResizeResult(response.data);
      
      // Create preview of resized image
      const previewResponse = await apiClient.get(
        `${BACKEND_URL}${response.data.download_url}?session_id=${sessionId}`,
        {
          responseType: 'blob',
        }
      );
      
      const previewUrl = URL.createObjectURL(previewResponse.data);
      setResizedPreview(previewUrl);
      
      toast({
        title: "Success",
        description: "Image resized successfully",
      });
    } catch (error) {
      console.error('Resize error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to resize image",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!resizeResult) return;
    
    try {
      const response = await apiClient.get(
        `${BACKEND_URL}${resizeResult.download_url}?session_id=${sessionId}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', resizeResult.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Downloaded ${resizeResult.name}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Resize Image Online — Adjust Image Dimensions Easily | AllSmallTooLS</title>
        <meta name="description" content="Quickly resize images by width and height. Free online image resizer — simple, fast, and works on all devices." />
        <meta name="keywords" content="resize image, image resizer, change image size, photo resize online, resize jpg png" />
        <link rel="canonical" href="https://allsmalltools.com/resize-image" />
      </Helmet>

      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
              <RefreshCw className="h-8 w-8 text-purple-500" />
              <span>Resize Image</span>
            </h1>
            <p className="text-gray-600">Change image dimensions or scale by percentage</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload & Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Upload & Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Image Upload */}
            {!uploadedImage ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  uploadingFile ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  dragActive 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={uploadingFile ? undefined : handleDrag}
                onDragLeave={uploadingFile ? undefined : handleDrag}
                onDragOver={uploadingFile ? undefined : handleDrag}
                onDrop={uploadingFile ? undefined : handleDrop}
                onClick={uploadingFile ? undefined : () => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="hidden"
                  disabled={uploadingFile}
                />
                
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                  dragActive ? 'bg-purple-100' : 'bg-gray-100'
                }`}>
                  <Upload className={`h-8 w-8 ${dragActive ? 'text-purple-600' : 'text-gray-400'}`} />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {uploadingFile ? 'Loading Image...' : 'Upload Image'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {uploadingFile ? 'Please wait...' : 'Drag and drop your image here, or click to browse'}
                </p>
                <p className="text-sm text-gray-500">
                  Supported formats: JPG, PNG, WebP, BMP, GIF • Max 10MB
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      <img 
                        src={uploadedImage.preview} 
                        alt="Original" 
                        className="w-24 h-24 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{uploadedImage.name}</p>
                        <p className="text-sm text-gray-500">{uploadedImage.size}</p>
                        <p className="text-sm text-gray-500">
                          Original Size: {originalDimensions.width} x {originalDimensions.height} px
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveImage}
                        className="flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Preset Sizes */}
                <div className="space-y-2">
                  <Label>Quick Presets</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {presetSizes.map((preset) => (
                      <Button
                        key={preset.name}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePresetSelect(preset)}
                        className="text-xs"
                      >
                        {preset.name}
                        <span className="ml-1 text-gray-500">({preset.width}x{preset.height})</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Resize Mode */}
                <div className="space-y-2">
                  <Label>Resize Mode</Label>
                  <RadioGroup value={resizeMode} onValueChange={setResizeMode}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="dimensions" id="dimensions" />
                      <Label htmlFor="dimensions">By Dimensions (px)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="percentage" id="percentage" />
                      <Label htmlFor="percentage">By Percentage (%)</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Dimensions Input */}
                {resizeMode === 'dimensions' && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Label>Maintain Aspect Ratio</Label>
                      <Switch
                        checked={maintainAspectRatio}
                        onCheckedChange={setMaintainAspectRatio}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Width (px)</Label>
                        <Input
                          type="number"
                          value={width}
                          onChange={(e) => handleWidthChange(e.target.value)}
                          placeholder="Width"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Height (px)</Label>
                        <Input
                          type="number"
                          value={height}
                          onChange={(e) => handleHeightChange(e.target.value)}
                          placeholder="Height"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Percentage Input */}
                {resizeMode === 'percentage' && (
                  <div className="space-y-2">
                    <Label>Scale Percentage</Label>
                    <Input
                      type="number"
                      value={percentage}
                      onChange={(e) => setPercentage(e.target.value)}
                      placeholder="Percentage"
                      min="1"
                      max="500"
                    />
                    <p className="text-xs text-gray-500">
                      New size will be: {Math.round(originalDimensions.width * percentage / 100)} x {Math.round(originalDimensions.height * percentage / 100)} px
                    </p>
                  </div>
                )}

                <Button 
                  onClick={handleResize}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                >
                  {loading ? 'Resizing...' : 'Resize Image'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent>
            {resizeResult ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Resize Complete!</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    New dimensions: {resizeResult.dimensions}
                  </p>
                </div>

                {/* Resized Image Preview */}
                {resizedPreview && (
                  <div className="space-y-2">
                    <Label>Resized Image Preview</Label>
                    <div className="border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center p-4">
                      <img 
                        src={resizedPreview} 
                        alt="Resized" 
                        className="max-w-full max-h-96 object-contain"
                      />
                    </div>
                  </div>
                )}

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{resizeResult.name}</p>
                        <p className="text-sm text-gray-500">{resizeResult.size}</p>
                      </div>
                      <Button
                        onClick={handleDownload}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No resize yet</p>
                <p className="text-sm">Upload an image and set dimensions to resize</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Why Use Our Resize Image Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Image Resizer?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Maximize2 className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Flexible Resizing</h3>
              <p className="text-gray-600">
                Resize images by exact dimensions or percentage. Use social media presets for Instagram, Facebook, Twitter. Maintain aspect ratio or set custom sizes.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Quality Preserved</h3>
              <p className="text-gray-600">
                Our image resizer maintains high quality during the resize process. Smart algorithms ensure your images look great at any size.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Instant Results</h3>
              <p className="text-gray-600">
                Resize images instantly with our fast online tool. No software installation or registration required. Preview before downloading.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Whether you need to resize images for web, social media, or printing – our free image resizer makes it simple. Perfect for optimizing images for faster loading, meeting size requirements, or creating thumbnails. Try our resize image tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/convert-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Convert Images
              </Link>
              <Link to="/compress-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Compress Images
              </Link>
              <Link to="/remove-background" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Remove Background
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Resize Images Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Resize Images Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Upload Your Image:</strong> Click the upload area or drag and drop your image. Our resizer supports JPG, PNG, WebP, and more formats.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Choose Resize Method:</strong> Select dimensions (pixels) or percentage scaling. Use presets for social media sizes or enter custom dimensions.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Set Dimensions:</strong> Enter width and height, or scale by percentage. Toggle aspect ratio lock to maintain image proportions.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download Resized Image:</strong> Click "Resize Image" and preview the result. Download your resized image in the original format.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is image resizing free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our image resizer is completely free. Resize unlimited images without registration, watermarks, or hidden fees.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Will resizing reduce image quality?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Enlarging images may reduce quality. Reducing size typically maintains quality. Our tool uses smart algorithms to preserve maximum quality when resizing images.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">What is aspect ratio?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Aspect ratio is the proportional relationship between image width and height. Maintaining aspect ratio prevents distortion when resizing images.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Can I resize multiple images at once?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Currently, our tool resizes one image at a time to ensure quality and precision. Process multiple images by repeating the quick resize workflow.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default ResizeImagePage;
